import React, { useEffect, useState } from "react";
import axios from "axios";
import classnames from "classnames";
import LogoDark from "assets/Logo";
import { withNotification } from "components/common";
import { connect } from "react-redux";
import * as actions from "state/actions/user";
import { Redirect } from "react-router-dom";
import useMount from "utils/hooks/useMount";
import passwordValidator from "password-validator";
import { track } from "../../../utils/analytics";

const md5 = require("md5");

const isPasswordValid = ({ newPassword1, newPassword2, setErrorMessage }) => {
  setErrorMessage(null);
  if (!newPassword1 || !newPassword2) {
    setErrorMessage("You must enter a password in both fields");
    track("DASHBOARD_PASSWORD_RESET_STEP2_FAILURE", {
      message: "One or more fields empty"
    });
    return;
  }
  if (newPassword1 !== newPassword2) {
    setErrorMessage("New passwords do not match");
    track("DASHBOARD_PASSWORD_RESET_STEP2_FAILURE", {
      message: "New Passwords do not match!"
    });
    return;
  }

  setErrorMessage(null);

  const schema = new passwordValidator();

  const minimumCharacters = 8;
  const maximumCharacters = 50;

  schema
    .is()
    .min(minimumCharacters) // Minimum length 8
    .is()
    .max(maximumCharacters) // Maximum length 100
    .has()
    .uppercase() // Must have uppercase letters
    .has()
    .lowercase() // Must have lowercase letters
    .has()
    .digits() // Must have digits
    .has()
    .not()
    .spaces() // Should not have spaces
    .is()
    .not()
    .oneOf(["Passw0rd", "Password123"]); // Blacklist these values

  const result = schema.validate(newPassword1, { list: true });
  if (result && result.length === 0) return true;

  if (result.includes("min")) {
    setErrorMessage(
      `Password must contain at least ${minimumCharacters} characters`
    );
  } else if (result.includes("max")) {
    setErrorMessage(
      `Password must contain ${maximumCharacters} characters at most`
    );
  } else if (result.includes("uppercase")) {
    setErrorMessage(`Password must contain uppercase characters`);
  } else if (result.includes("lowercase")) {
    setErrorMessage(`Password must contain lowercase characters`);
  } else if (result.includes("digits")) {
    setErrorMessage(`Password must contain a number`);
  } else if (result.includes("spaces")) {
    setErrorMessage(`Password must not contain any spaces`);
  } else if (result.includes("oneOf")) {
    setErrorMessage(
      `Your password is too generic. Please use a more unique password.`
    );
  }

  track("DASHBOARD_PASSWORD_RESET_STEP2_FAILURE", {
    message: "Failed password validation"
  });
  return false;
};

const performPasswordConfirmation = (
  { email, code, newPassword1, newPassword2 },
  setLoading,
  setErrorMessage,
  setGoToLogin
) => {
  setLoading(true);
  if (
    !isPasswordValid({
      newPassword1,
      newPassword2,
      setErrorMessage,
      setLoading
    })
  ) {
    setLoading(false);
    return;
  }
  axios
    .post(`${process.env.REACT_APP_BACKEND_URL}/confirmpassword`, {
      email,
      code,
      newPassword: md5(newPassword1)
    })
    .then(response => {
      track("DASHBOARD_PASSWORD_RESET_STEP2_SUCCESS");
      setGoToLogin(true);
    })
    .catch(error => {
      setErrorMessage("Tomo, we have a problem!");
      track("DASHBOARD_PASSWORD_RESET_STEP2_FAILURE", {
        message: error
      });
      console.log("Failed to Initiate PasswordRecovery: ", error);
    })
    .finally(() => {
      setLoading(false);
    });
};

function ConfirmPassword({ location }) {
  const [newPassword1, setNewPassword1] = useState(null);
  const [newPassword2, setNewPassword2] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [codeStatus, setCodeStatus] = useState(false);
  const [goToLogin, setGoToLogin] = useState(false);

  const { search } = location;
  const params = new URLSearchParams(search);
  const code = params.get("code");
  const email = params.get("email");

  useMount(() => {
    console.log("useEffect called");
    setLoading(true);
    axios
      .post(`${process.env.REACT_APP_BACKEND_URL}/verifycode`, {
        email,
        code
      })
      .then(resp => {
        console.log("OKKKK");
        setLoading(false);
        setCodeStatus(resp.status === 200);
      })
      .catch(err => {
        console.log("NOT OKKKK", err);
        setLoading(false);
        setCodeStatus(false);
      });
  });

  useEffect(() => {
    if (goToLogin) {
      window.location.replace("/dashboard/login");
    }
  }, [goToLogin]);

  const handleSubmit = () => {
    performPasswordConfirmation(
      {
        email,
        code,
        newPassword1,
        newPassword2
      },
      setLoading,
      setErrorMessage,
      setGoToLogin
    );
  };

  track("DASHBOARD_PASSWORD_RESET_STEP2_LOADED");

  return goToLogin ? (
    <Redirect to="/login" />
  ) : !codeStatus ? (
    loading ? (
      ``
    ) : (
      `Page Not Found`
    )
  ) : (
    <div className="bg-gray-100 flex h-screen outerContainer w-screen">
      <div className="bg-white block border innerContainer max-w-sm mx-auto px-10 py-10 self-center shadow-lg rounded">
        <LogoDark className="text-4xl mt-5 mb-10 mx-auto" />
        <input
          type="password"
          name="email"
          value={newPassword1}
          placeholder="Enter new password"
          className="w-full my-2 p-2 border border-gray-300 rounded bg-white inputField text-lg outline-none"
          onChange={e => {
            setNewPassword1(e.target.value);
          }}
        />
        <input
          type="password"
          name="email"
          value={newPassword2}
          placeholder="Re-enter new password"
          className="w-full my-2 p-2 border border-gray-300 rounded bg-white inputField text-lg outline-none"
          onChange={e => {
            setNewPassword2(e.target.value);
          }}
        />
        <div className={errorMessage ? "text-red-600 p-2" : "hidden"}>
          {errorMessage}
        </div>
        <input
          type="button"
          name="button"
          onClick={() => handleSubmit()}
          value="Reset Password"
          className={classnames(
            "button w-full p-4 bg-green-primary text-white rounded-sm loginButton shadow-lg rounded mt-3 font-bold cursor-pointer outline-none hover:shadow-xl border-none",
            loading ? "disabled opacity-50 cursor-not-allowed" : ""
          )}
        />
        <div className="w-full mt-3 pt-2 text-black text-center">
          <button
            onClick={() => setGoToLogin(true)}
            className="underline w-full py-2 text-black"
            type="button"
          >
            Back to login
          </button>
        </div>
      </div>
    </div>
  );
}

export default connect(
  state => ({}),
  actions
)(withNotification(ConfirmPassword));
